import {getQueryClient} from '@github-ui/query-client'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {getRepositorySearchQuery, type RepositoryPickerOwner} from '../utils/get-repository-search-query'
import type {OrgRepositoryPickerOrganization, OrgRepositoryPickerRepository} from './org-repository-picker-types'

const ACCOUNT_ISSUE_ORGANIZATIONS_ENDPOINT = '/_ghui/item-pickers/account_issue_organizations'
const TOP_REPOSITORIES_ENDPOINT = '/_ghui/item-pickers/top_repositories'
const SEARCH_REPOSITORIES_ENDPOINT = '/_ghui/item-pickers/repositories_search_full'

// Shape returned by the account_issue_organizations endpoint.
interface AccountIssueOrganizationResponse {
  orgLogin: string
  orgName: string
  orgAvatarUrl: string
  repositoryOwner: string
  repositoryName: string
}

// Minimal repository node shape shared by the top-repositories and search endpoints.
interface RepositoryNode {
  id: string
  name: string
  nameWithOwner: string
  owner: {login: string}
  isPrivate?: boolean
  isArchived?: boolean
  isLocked?: boolean
  hasIssuesEnabled?: boolean
}

function accountIssueOrganizationsQueryKey(orgLogin?: string) {
  return ['item-picker', 'org-repository-picker', 'organizations', orgLogin ?? ''] as const
}

function topRepositoriesQueryKey(owner?: RepositoryPickerOwner) {
  return ['item-picker', 'org-repository-picker', 'top-repositories', owner?.login ?? ''] as const
}

function searchRepositoriesQueryKey(searchQuery: string, owner?: RepositoryPickerOwner) {
  return ['item-picker', 'org-repository-picker', 'search-repositories', owner?.login ?? '', searchQuery] as const
}

function toRepository(node: RepositoryNode): OrgRepositoryPickerRepository {
  return {
    id: node.id,
    name: node.name,
    nameWithOwner: node.nameWithOwner,
    ownerLogin: node.owner.login,
    isPrivate: node.isPrivate ?? false,
  }
}

async function fetchAccountIssueOrganizationsFromServer(
  orgLogin?: string,
  signal?: AbortSignal,
): Promise<OrgRepositoryPickerOrganization[]> {
  const query = new URLSearchParams()
  if (orgLogin) query.set('owner', orgLogin)
  const suffix = query.toString()
  const response = await reactFetchJSON(`${ACCOUNT_ISSUE_ORGANIZATIONS_ENDPOINT}${suffix ? `?${suffix}` : ''}`, {
    signal,
  })
  if (!response.ok) throw new Error(`Account issue organizations fetch failed (${response.status})`)

  const json = (await response.json()) as {organizations?: AccountIssueOrganizationResponse[]}
  return (json.organizations ?? []).map(org => ({
    login: org.orgLogin,
    name: org.orgName,
    avatarUrl: org.orgAvatarUrl,
    repositoryOwner: org.repositoryOwner,
    repositoryName: org.repositoryName,
  }))
}

async function fetchTopRepositoriesFromServer(
  owner?: RepositoryPickerOwner,
  signal?: AbortSignal,
): Promise<OrgRepositoryPickerRepository[]> {
  const query = new URLSearchParams({hasIssuesEnabled: 'true'})
  if (owner) query.set('owner', owner.login)
  const response = await reactFetchJSON(`${TOP_REPOSITORIES_ENDPOINT}?${query.toString()}`, {signal})
  if (!response.ok) throw new Error(`Top repositories fetch failed (${response.status})`)

  const json = (await response.json()) as {
    data?: {viewer?: {topRepositories?: {edges?: Array<{node?: RepositoryNode} | null> | null}}}
  }
  const edges = json.data?.viewer?.topRepositories?.edges
  if (edges != null && !Array.isArray(edges)) throw new Error('Top repositories query returned errors')

  return (edges ?? [])
    .flatMap(edge => (edge?.node ? [edge.node] : []))
    .filter(node => !node.isArchived && !node.isLocked)
    .map(toRepository)
}

async function fetchSearchRepositoriesFromServer(
  searchQuery: string,
  owner?: RepositoryPickerOwner,
  signal?: AbortSignal,
): Promise<OrgRepositoryPickerRepository[]> {
  // When scoped to an owner, build a search string with the `org:`/`user:` qualifier so results
  // stay within that owner. Unscoped, send the raw query for the broader (future global) flow.
  const serverQuery = owner ? getRepositorySearchQuery(searchQuery, {owner}) : searchQuery
  const query = new URLSearchParams({searchQuery: serverQuery})
  const response = await reactFetchJSON(`${SEARCH_REPOSITORIES_ENDPOINT}?${query.toString()}`, {signal})
  if (!response.ok) throw new Error(`Repository search failed (${response.status})`)

  const json = (await response.json()) as {data?: {search?: {nodes?: Array<RepositoryNode | null> | null}}}
  const nodes = json.data?.search?.nodes
  if (nodes != null && !Array.isArray(nodes)) throw new Error('Repository search query returned errors')

  // The search endpoint has no hasIssuesEnabled filter, so exclude issue-disabled repos client-side.
  return (nodes ?? [])
    .flatMap(node => (node ? [node] : []))
    .filter(node => !node.isArchived && !node.isLocked && node.hasIssuesEnabled !== false)
    .map(toRepository)
}

export async function fetchAccountIssueOrganizations(orgLogin?: string): Promise<OrgRepositoryPickerOrganization[]> {
  return getQueryClient().fetchQuery({
    queryKey: accountIssueOrganizationsQueryKey(orgLogin),
    queryFn: ({signal}) => fetchAccountIssueOrganizationsFromServer(orgLogin, signal),
  })
}

export async function fetchTopRepositories(owner?: RepositoryPickerOwner): Promise<OrgRepositoryPickerRepository[]> {
  return getQueryClient().fetchQuery({
    queryKey: topRepositoriesQueryKey(owner),
    queryFn: ({signal}) => fetchTopRepositoriesFromServer(owner, signal),
  })
}

export async function fetchSearchRepositories(
  searchQuery: string,
  owner?: RepositoryPickerOwner,
): Promise<OrgRepositoryPickerRepository[]> {
  return getQueryClient().fetchQuery({
    queryKey: searchRepositoriesQueryKey(searchQuery, owner),
    queryFn: ({signal}) => fetchSearchRepositoriesFromServer(searchQuery, owner, signal),
  })
}
